import apiV2Service from 'common/src/modules/api/api-v2.service';
import {
  ContentFeed,
  ContentFeedColumnsSchema,
  ContentFeedFieldMapping,
  ContentFeedFieldMappingResponse,
  ContentFeedHistoryUpdate,
  ContentFeedRule,
  FeedSourceWithFile,
  FeedSourceWithGoogleSheet,
  RemoteContentFeed,
  RemoteContentFeedHistoryUpdate,
} from 'common/src/modules/contentFeeds/contentFeeds.types';
import {
  RemoteAiContentBrief,
  RemoteAiContentGenerationSetup,
} from 'common/src/modules/contentFeeds/contentFeeds.aiContentTypes';
import {
  toClientContentFeed,
  toClientFeedUpdateHistory,
} from 'common/src/modules/contentFeeds/contentFeeds.dataConverters';
import {handleApiError} from 'common/src/modules/errorHandling/handleApiError';
import {MacroItem} from 'common/src/modules/macros/macros.types';
import {AxiosProgressEvent, CancelToken} from 'axios';
import {fileUploadApi} from 'common/src/modules/fileUpload/fileUpload.api';
import parseFileName from 'common/src/utils/fileUtils/parseFileName';
import {JobExecutionParams} from 'common/src/modules/jobs/jobs.types';
import waitForJob from 'common/src/modules/jobs/utils/waitForJob';

const basePath = 'ContentFeed';

async function getContentFeeds() {
  return apiV2Service
    .executeGetRequest<RemoteContentFeed[]>(basePath)
    .then(({data: feeds}) => feeds.map(toClientContentFeed))
    .catch((error) => {
      handleApiError(error);

      return [] as ContentFeed[];
    });
}

async function addFeed() {
  return apiV2Service
    .executePostRequest<number>(`${basePath}`)
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);

      return 0;
    });
}

async function addAiGeneratedContentFeed(request: {name: string; templateId: number}) {
  return apiV2Service
    .executePostRequest<number>(`${basePath}/ai`, {}, request)
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);

      return 0;
    });
}

async function duplicateFeed(feedId: number) {
  try {
    const duplicateFeedResponse = await apiV2Service.executePostRequest<number>(
      `${basePath}/${feedId}/duplicate`,
    );
    await updateInventory(duplicateFeedResponse.data);

    return duplicateFeedResponse.data;
  } catch (error) {
    handleApiError(error);

    return 0;
  }
}

async function deleteFeed(feedId: number) {
  return apiV2Service.executeDeleteRequest<void>(`${basePath}/${feedId}`).catch(handleApiError);
}

async function getFeedSignals(feedId: number) {
  return apiV2Service
    .executeGetRequest<MacroItem[]>(`${basePath}/${feedId}/columnSignals`)
    .then((response) => response.data);
}

async function getFeed(feedId: number) {
  return apiV2Service
    .executeGetRequest<RemoteContentFeed>(`${basePath}/${feedId}`)
    .then(({data: feed}) => toClientContentFeed(feed));
}

async function updateBrief(feedId: number, brief: RemoteAiContentBrief) {
  return apiV2Service
    .executePutRequest<void>(`${basePath}/${feedId}/brief`, {}, brief as unknown as Record<string, unknown>)
    .catch(handleApiError);
}

async function updateTemplateId(
  feedId: number,
  request: {templateId: number | null},
) {
  return apiV2Service
    .executePutRequest<void>(`${basePath}/${feedId}/templateId`, {}, request)
    .catch(handleApiError);
}

async function updateFeedColumnsSchema(feedId: number, schema: ContentFeedColumnsSchema) {
  return apiV2Service
    .executePutRequest<void>(
      `${basePath}/${feedId}/feedColumnsSchema`,
      {},
      schema as unknown as Record<string, unknown>,
    )
    .catch(handleApiError);
}

async function updateColumnGenerationSetup(
  feedId: number,
  request: {columnKey: string; generationSetup: RemoteAiContentGenerationSetup},
) {
  return apiV2Service
    .executePutRequest<void>(`${basePath}/${feedId}/feedColumnsSchema/generation-setup`, {}, request)
    .catch(handleApiError);
}

async function fetchHistoryUpdates(feedId: number): Promise<ContentFeedHistoryUpdate[]> {
  return apiV2Service
    .executeGetRequest<RemoteContentFeedHistoryUpdate[]>(`${basePath}/${feedId}/updateHistory`)
    .then(({data: historyUpdates}) => historyUpdates.map(toClientFeedUpdateHistory));
}

async function uploadFile(
  feedId: number,
  file: File,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
  cancelToken?: CancelToken,
) {
  const urlObj = await fileUploadApi.getPresignedUrlForContentFeedFileUpload({
    feedId,
    contentType: file.type,
    fileExtension: parseFileName(file.name).fileExtension || '',
  });

  await fileUploadApi.uploadFileToS3({
    file,
    cancelToken,
    url: urlObj.presignedUrl,
    onUploadProgress,
    contentType: file.type,
  });

  return urlObj;
}

async function updateFeedName(feedId: number, name: string) {
  return apiV2Service
    .executePutRequest<void>(`${basePath}/${feedId}/name`, {name})
    .catch(handleApiError);
}

async function updateFeedSource(
  feedId: number,
  source: FeedSourceWithFile | FeedSourceWithGoogleSheet,
  schedule?: {cronExpression: string; timeZone: string},
) {
  return apiV2Service
    .executePutRequest<void>(
      `${basePath}/${feedId}/source`,
      {},
      {
        ...source,
        ...(schedule && {
          cronExpression: schedule.cronExpression,
          timeZone: schedule.timeZone,
        }),
      },
    )
    .catch(handleApiError);
}

function updateInventory(feedId: number) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`${basePath}/${feedId}/updateInventory`)
    .then(({data}) => waitForJob({jobId: data.executionId, jobName: 'Update Content Items'}))
    .catch(handleApiError);
}

function getInventoryFilePath(feedId: number) {
  return apiV2Service
    .executeGetRequest<string>(`${basePath}/${feedId}/downloadFile`)
    .then(({data: url}) => url);
}

async function getMapping(feedId: number) {
  return apiV2Service
    .executeGetRequest<ContentFeedFieldMappingResponse>(`${basePath}/${feedId}/mapping`)
    .then(({data}) => data.map);
}

async function updateMapping(feedId: number, mapping: ContentFeedFieldMapping[]) {
  return Promise.all([
    apiV2Service.executePutRequest<void>(`${basePath}/${feedId}/mapping`, {}, {map: mapping}),
    updateInventory(feedId),
  ]).catch(handleApiError);
}

async function getRules(feedId: number): Promise<ContentFeedRule[]> {
  return apiV2Service
    .executeGetRequest<ContentFeedRule[]>(`${basePath}/${feedId}/rules`)
    .then(({data}) => data);
}

async function updateRules(feedId: number, rules: ContentFeedRule[]) {
  return apiV2Service.executePutRequest<void>(`${basePath}/${feedId}/rules`, {}, {rules});
}

async function activateDraftItems(feedId: number, itemIds: number[]): Promise<number> {
  const {
    data: {executionId},
  } = await apiV2Service.executePostRequest<{executionId: number}>(
    `${basePath}/${feedId}/activate`,
    {},
    {itemIds},
  );

  return executionId;
}

async function searchFeeds(prompt: string) {
  const {data} = await apiV2Service.executeGetRequest<{
    contentFeeds: RemoteContentFeed[];
    filtersExplanation: string;
  }>(`${basePath}/SearchContentFeeds`, {prompt});

  return data;
}

export const contentFeedsApi = {
  getContentFeeds,
  getFeed,
  addFeed,
  addAiGeneratedContentFeed,
  duplicateFeed,
  deleteFeed,
  updateFeedName,
  updateFeedSource,
  updateBrief,
  updateTemplateId,
  updateFeedColumnsSchema,
  updateColumnGenerationSetup,
  getFeedSignals,
  fetchHistoryUpdates,
  uploadFile,
  updateInventory,
  getInventoryFilePath,
  getMapping,
  updateMapping,
  getRules,
  updateRules,
  activateDraftItems,
  searchFeeds,
};
